/**
 * Shared HiGHS Web Worker plumbing.
 *
 * The packing, coverage, and TSP clients build a CPLEX-LP-format model string
 * and hand it to the same worker, which runs the HiGHS MILP solver (compiled
 * to WebAssembly) off the main thread so solving never freezes the host
 * game's UI.
 *
 * The engine is self-hosted: highs.js + highs.wasm come from the pinned
 * `highs` npm package, bundled by the consuming game's Vite as hashed assets
 * (the `?url` imports below). The worker is told their same-origin URLs with
 * each solve message — no CDN at runtime, so first-visit latency is bounded
 * by the game's own host and the browser's cross-site cache partitioning
 * never forces a cold re-download.
 */

// Resolved by the consuming app's Vite (and by Vitest) to asset URL strings.
// The `highs` package exports "." → build/highs.js and "./runtime" → highs.wasm.
import highsJsAsset from "highs?url";
import highsWasmAsset from "highs/runtime?url";

/** Shape of the raw solution object returned by highs-js. */
export interface HighsColumn { Primal: number }
export interface HighsSolution {
  Status: string;
  ObjectiveValue?: number;
  Columns: Record<string, HighsColumn>;
}

/** A solved model plus the honest engine time. */
export interface SolveLpResult {
  sol: HighsSolution;
  /** Pure `highs.solve()` time, measured inside the worker — excludes the
   *  one-time WASM download/initialisation and any message-queue wait, so it
   *  is the number the games may honestly display as "solved in X ms". */
  solveMs: number;
}

/** Absolute same-origin URLs for the engine files, resolved lazily so merely
 *  importing this module never touches `document` (e.g. in node tests). */
function engineUrls(): { jsUrl: string; wasmUrl: string } {
  const base = typeof document !== "undefined" ? document.baseURI : self.location.href;
  return {
    jsUrl: new URL(highsJsAsset, base).href,
    wasmUrl: new URL(highsWasmAsset, base).href,
  };
}

let worker: Worker | null = null;
function getWorker(): Worker {
  if (!worker) {
    // Vite bundles this classic worker as a hashed asset. Works under any
    // deploy sub-path and needs no per-game public/ copy.
    worker = new Worker(new URL("./highs.worker.js", import.meta.url));
  }
  return worker;
}

let seq = 0;
/** Solve a CPLEX-LP-format model string with HiGHS in the worker. */
export function solveLp(lp: string, options: Record<string, unknown>): Promise<SolveLpResult> {
  const w = getWorker();
  const id = ++seq;
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      w.removeEventListener("message", onMsg);
      w.removeEventListener("error", onErr);
    };
    const onMsg = (e: MessageEvent) => {
      const d = e.data as { id: number; ok: boolean; sol?: HighsSolution; solveMs?: number; error?: string };
      if (d.id !== id) return;
      cleanup();
      if (d.ok && d.sol) resolve({ sol: d.sol, solveMs: d.solveMs ?? 0 });
      else reject(new Error(d.error || "HiGHS solver error"));
    };
    // If the worker script itself fails to load, reject rather than leaving
    // the solve promise hanging forever.
    const onErr = (e: ErrorEvent) => {
      cleanup();
      reject(new Error("HiGHS worker failed to load: " + (e.message || "unknown error")));
    };
    w.addEventListener("message", onMsg);
    w.addEventListener("error", onErr);
    w.postMessage({ id, lp, options, engine: engineUrls() });
  });
}

/**
 * Kick off worker creation + WASM download/initialisation during gameplay so
 * the end-of-run solve is effectively instant. A trivial 1-variable model
 * forces the module to initialise. Safe to call multiple times.
 */
export function warmUpHighs(): void {
  const lp = "Maximize\n obj: x0\nSubject To\n c0: x0 <= 1\nBounds\n 0 <= x0 <= 1\nBinary\n x0\nEnd\n";
  solveLp(lp, { presolve: "on" }).catch(() => { /* ignore warmup failures */ });
}
